import {
  IActionLog,
  ICustomerItem,
  IKavDataItem,
  IOrderItem,
} from "../../../../../modules/API/Drivers/models/kavData";

/**
 * map, сохраняющий ссылки: если ни один элемент не изменился, возвращает
 * исходный массив. Держит identity нетронутых веток дерева — карточки, которым
 * ничего не пришло, не перерисовываются.
 */
const mapPreserve = <T>(list: T[], fn: (item: T) => T): T[] => {
  let changed = false;
  const next = list.map((item) => {
    const mapped = fn(item);
    if (mapped !== item) changed = true;
    return mapped;
  });
  return changed ? next : list;
};

const mapCustomersOf = (
  kav: IKavDataItem,
  fn: (customer: ICustomerItem) => ICustomerItem
): IKavDataItem => {
  const customers = mapPreserve(kav.Customers, fn);
  return customers === kav.Customers ? kav : { ...kav, Customers: customers };
};

const mapOrdersOf = (
  customer: ICustomerItem,
  fn: (order: IOrderItem) => IOrderItem
): ICustomerItem => {
  const orders = mapPreserve(customer.Orders, fn);
  return orders === customer.Orders ? customer : { ...customer, Orders: orders };
};

/**
 * Патчит LastAction у теудот, отмеченных в selectedOrders, во всех кавах.
 * Матч по Delivery_Code + Delivery_Sub + OrderID при Selected === true.
 * Легаси намеренно не фильтрует по каву — поведение сохранено.
 */
export const applyOrdersConfirm = (
  data: IKavDataItem[],
  selectedOrders: IOrderItem[],
  action: number,
  actionDescription: string
): IKavDataItem[] =>
  mapPreserve(data, (kav) =>
    mapCustomersOf(kav, (customer) =>
      mapOrdersOf(customer, (order) => {
        const isConfirmed = selectedOrders.some(
          (selected) =>
            selected.Delivery_Code === order.Delivery_Code &&
            selected.Delivery_Sub === order.Delivery_Sub &&
            selected.OrderID === order.OrderID &&
            selected.Selected
        );
        if (!isConfirmed) return order;
        return {
          ...order,
          LastAction: {
            ...order.LastAction,
            Action: action,
            ActionDescription: actionDescription,
            ActionFlag: action === 1,
          },
          Selected: false,
        };
      })
    )
  );

/**
 * Сбрасывает счётчики аризот и патчит LastAction у клиента во всех кавах.
 * customerID === undefined — no-op: легаси сравнивало с `customerData?.CustomerID`,
 * что при отсутствии клиента никогда не совпадало.
 */
export const applyPackageAction = (
  data: IKavDataItem[],
  customerID: number | undefined,
  action: number,
  actionDescription: string
): IKavDataItem[] => {
  if (customerID === undefined) return data;
  return mapPreserve(data, (kav) =>
    mapCustomersOf(kav, (customer) =>
      customer.CustomerID !== customerID
        ? customer
        : {
            ...customer,
            PO_Num: 0,
            PD_Num: 0,
            LastAction: {
              ...customer.LastAction,
              Action: action,
              ActionDescription: actionDescription,
              ActionFlag: false,
            },
          }
    )
  );
};

/** Проставляет DriverStatus теуде внутри конкретного кава. */
export const applyProblemStatus = (
  data: IKavDataItem[],
  kavID: number,
  orderID: number,
  status: number
): IKavDataItem[] =>
  mapPreserve(data, (kav) => {
    if (kav.AreaID !== kavID) return kav;
    return mapCustomersOf(kav, (customer) =>
      mapOrdersOf(customer, (order) =>
        order.OrderID === orderID ? { ...order, DriverStatus: status } : order
      )
    );
  });

/** Добавляет запись лога в Actions конкретного кава. */
export const appendKavAction = (
  data: IKavDataItem[],
  kavID: number,
  log: IActionLog
): IKavDataItem[] =>
  mapPreserve(data, (kav) =>
    kav.AreaID === kavID ? { ...kav, Actions: [...kav.Actions, log] } : kav
  );

/** Инвертирует Selected у всех теудот с указанным OrderID. */
export const toggleOrderSelection = (
  orders: IOrderItem[],
  orderID: number
): IOrderItem[] =>
  mapPreserve(orders, (order) =>
    order.OrderID === orderID ? { ...order, Selected: !order.Selected } : order
  );
